import React, { useState } from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import ProjectAdminFormPayment from "./ProjectAdminFormPayment";
import ProjectAdminFormTrackingAndSocialNetwork from "./ProjectAdminFormTrackingAndSocialNetwork";
import ProjectAdminFormUrl from "./ProjectAdminFormUrl";
import ProjectAdminTranslations from "./ProjectAdminTranslations";
import { TypeEnum } from "../../../api/ProjectAPI.types";
import { CONTENT_MAX_WIDTH_STYLE } from "../../../constants/size";
import "../../../css/admin/project.scss";
import "../../../css/admin/utils/item-status.scss";
import Block from "../../block/Block";
import Breadcrumbs from "../../utils/Breadcrumbs";
import ContainerHeader from "../../utils/ContainerHeader";
import { getPageLinkTranslation } from "../../utils/Translations";

import type { ChangeEvent, JSX } from "react";

import type {
  PageLinkTranslation,
  Project,
  ProjectTranslation,
  StorefrontProjectLogin,
  StorefrontProjectLoginConfig,
  UpdatedProject,
  UpdatedProjectTranslation,
  UpdatedStorefrontProjectLogin,
} from "../../../api/ProjectAPI.types";
import type { AppState } from "../../../reducers";

const containerHeaderLinks = {
  en: "https://support.okast.tv/en/support/solutions/articles/103000097762-configure-the-parameters-of-your-project-title-description-favicon-stripe-account-footer-links-",
  fr: "https://support.okast.tv/fr/support/solutions/articles/103000097762-configurez-les-param%C3%A8tres-de-votre-projet-titre-description-favicon-compte-stripe-liens-du-foote",
};

interface ProjectAdminFormProps {
  project: Project | UpdatedProject;
  onSubmit: (project: UpdatedProject, skipValidation?: boolean) => void;
  type: string;
  hideBackButton?: boolean;
  itemIsValid: (project: UpdatedProject) => string[];
}

function ProjectAdminForm({
  project,
  onSubmit,
  type,
  hideBackButton,
  itemIsValid,
}: ProjectAdminFormProps): JSX.Element {
  const [currentProject, setCurrentProject] = useState<UpdatedProject>(project);
  const { t } = useTranslation("editor-project");
  const { projectSlug } = useParams<{ projectSlug: string }>();

  const projectStore = useSelector(
    (state: AppState) => state.fetchReducer.project,
  );

  const handleChangeTranslation = (
    key: keyof UpdatedProjectTranslation,
    translation: ProjectTranslation | UpdatedProjectTranslation,
    target: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>["target"],
  ): void => {
    const updatedProject = { ...currentProject };
    const targetIndex =
      updatedProject.translations_indexes.language?.[translation.language]?.[0];

    if (targetIndex !== undefined && updatedProject.translations[targetIndex]) {
      (updatedProject.translations[targetIndex][key] as string) = target.value;

      setCurrentProject(updatedProject);
    }
  };

  const onSave = (): void => {
    const pageLinksUpdated = currentProject.page_links.map((link) => {
      const translations: Omit<PageLinkTranslation, "uuid">[] =
        currentProject.translations.map((translation) => {
          const {
            title,
            url,
          }: {
            title: string;
            url: string;
          } = getPageLinkTranslation(link, translation);
          return {
            default: translation.default,
            language: translation.language,
            title,
            url,
          };
        });
      return {
        ...link,
        translations,
      };
    });

    onSubmit({
      ...currentProject,
      page_links: pageLinksUpdated,
    });
  };

  const handleChangeEnabled = (enabled: boolean): void => {
    setCurrentProject({
      ...currentProject,
      enabled,
    });
  };

  const handleChangeStorefrontProjectLoginAttr = (
    target: ChangeEvent<HTMLInputElement>["target"],
    storefrontProjectLoginName: string,
    attr: keyof StorefrontProjectLoginConfig,
    defaultConfig: StorefrontProjectLoginConfig,
  ): void => {
    // Modify if storefront_project_login exist
    let existingItem:
      | StorefrontProjectLogin
      | UpdatedStorefrontProjectLogin
      | null = null;
    const updatedItem = { ...currentProject };

    updatedItem.storefront_project_logins =
      updatedItem.storefront_project_logins.map((storefrontProjectLogin) => {
        if (
          storefrontProjectLogin.login_provider === storefrontProjectLoginName
        ) {
          existingItem = storefrontProjectLogin;

          return {
            ...storefrontProjectLogin,
            config: {
              ...storefrontProjectLogin.config,
              [attr]: target.value,
            },
          };
        }
        return storefrontProjectLogin;
      });

    if (existingItem) {
      let empty = true;
      Object.keys(defaultConfig).forEach((key) => {
        if (
          (existingItem as StorefrontProjectLogin).config[
            key as keyof StorefrontProjectLoginConfig
          ]
        )
          empty = false;
      });
      if (empty) {
        updatedItem.storefront_project_logins =
          updatedItem.storefront_project_logins.filter(
            (storefrontProjectLogin) =>
              storefrontProjectLogin.login_provider !==
              storefrontProjectLoginName,
          );
      }
    }

    // Create new if storefront_project_login not exist
    if (!existingItem) {
      const newStorefrontProjectLogin = {
        login_provider: storefrontProjectLoginName,
        config: {
          ...defaultConfig,
          [attr]: target.value,
        },
      };
      updatedItem.storefront_project_logins.push(newStorefrontProjectLogin);
    }

    setCurrentProject(updatedItem);
  };
  const isNew = type === "new";

  const breadcrumbs: {
    name: string;
    url?: string;
  }[] = [
    {
      name: t(type),
    },
  ];

  if (projectSlug === "new") {
    breadcrumbs.unshift({
      name: t("projectsTitle"),
      url: "/projects",
    });
  }

  return (
    <div
      className="editor-project-form-container editor-has-breadcrumbs mx-auto flex max-w-(--maxWidth) flex-col gap-3.75 [&_.section-separator:last-child]:border-b-0!"
      style={CONTENT_MAX_WIDTH_STYLE}
    >
      <Breadcrumbs items={breadcrumbs} />
      <ContainerHeader
        infoLinks={containerHeaderLinks}
        item={currentProject}
        itemStatus={currentProject.enabled ? "active" : "stop"}
        labelAttr="name"
        loading={projectStore.isFetching}
        onChangeState={handleChangeEnabled}
        onSave={onSave}
        separator={false}
        withBackBtn={!hideBackButton}
      />
      <Block>
        <ProjectAdminFormUrl
          isNew={isNew}
          project={currentProject}
          setProject={setCurrentProject}
          onChangeStorefrontProjectLoginAttr={
            handleChangeStorefrontProjectLoginAttr
          }
        />
      </Block>
      {!isNew && currentProject.type !== TypeEnum.Edu && (
        <Block>
          <ProjectAdminFormPayment
            project={currentProject}
            setProject={setCurrentProject}
          />
        </Block>
      )}
      <Block>
        <ProjectAdminTranslations
          itemIsValid={itemIsValid}
          onChangeTranslation={handleChangeTranslation}
          onSubmit={onSubmit}
          project={currentProject}
          setProject={setCurrentProject}
        />
      </Block>
      <Block>
        <ProjectAdminFormTrackingAndSocialNetwork
          project={currentProject}
          setProject={setCurrentProject}
        />
      </Block>
    </div>
  );
}

export default ProjectAdminForm;
